/**
 * קטגוריות — "באיזו קטגוריה אנחנו מוציאים יותר מהרגיל, ולמה" (spec §4.3, §6.2, §6.3).
 *
 * The stacked 6-month chart folds to 5 named series + "אחר (מאוחד)" (§6.2): the categories
 * that are currently OVER come first (so the month's story never disappears into "אחר"), then
 * by baseline descending. The selected category's panel carries the three stat tiles, the
 * 6-month trend with its baseline line, the threshold editor, sub-categories and top merchants.
 */

import { useState } from 'react';
import type { CategoriesResponse, CategoryDetailResponse, CategoryStat, GroupKey, SummaryResponse } from '../../../shared-types.ts';
import { GROUP_KEYS } from '../../../shared-types.ts';
import { fmtDM, fmtMoney, fmtPct, monthName, monthShort } from '../format.ts';
import { CATEGORY, he, plural } from '../i18n/he.ts';
import { Icon } from '../components/Icon.tsx';
import { CategoryIcon, Chip, EmptyState, Money, Section } from '../components/ui.tsx';
import { PeriodChip } from '../components/Shell.tsx';
import { Columns } from '../charts/Columns.tsx';
import { StackedColumns, type StackSeries } from '../charts/StackedColumns.tsx';
import { useApp } from '../hooks/useApp.tsx';
import { useQuery } from '../hooks/useQuery.ts';
import { cacheKey } from '../hooks/cache.ts';
import { useIsMobile } from '../hooks/useMediaQuery.ts';
import { Link, useRouter } from '../hooks/useRouter.tsx';
import { buildPath } from '../router.ts';

const MERGED = 'OTHER_MERGED';

function namedSeries(items: CategoryStat[]): GroupKey[] {
  const over = items.filter((stat) => stat.over);
  const rest = items.filter((stat) => !stat.over).sort((x, y) => y.baseline.agorot - x.baseline.agorot);
  return [...over, ...rest].slice(0, 5).sort((x, y) => y.baseline.agorot - x.baseline.agorot).map((stat) => stat.groupKey);
}

export function CategoriesScreen({ summary }: { summary: SummaryResponse | undefined }) {
  const { api, period } = useApp();
  const { route } = useRouter();
  const isMobile = useIsMobile();
  const selectedKey = (route.param as GroupKey | null) ?? null;

  const list = useQuery<CategoriesResponse>(cacheKey('categories', { period }), () => api.categories(period), { scopes: ['categories', 'summary'] });

  if (list.loading && !list.data) {
    return (
      <div className="screen">
        <div className="sk" style={{ height: '280px' }} />
        <div className="sk" style={{ height: '320px' }} />
      </div>
    );
  }
  if (!list.data) return <div className="screen"><EmptyState icon="alert" title={he.states.loadFailed} /></div>;

  const data = list.data;
  const stats = data.items;
  const selected = selectedKey && stats.find((stat) => stat.groupKey === selectedKey) ? selectedKey : (stats[0]?.groupKey ?? 'OTHER');
  const isSub = Boolean(route.param) && isMobile;

  const named = namedSeries(stats);
  const series: StackSeries[] = named.map((key, index) => ({ key, label: CATEGORY[key].label, series: index + 1 }));
  series.push({ key: MERGED, label: he.cats.otherMerged, series: 6 });
  const mergedKeys = GROUP_KEYS.filter((key) => !named.includes(key));

  const rows = data.monthly.map((month) => {
    const values: Record<string, number> = {};
    let total = 0;
    for (const item of series) {
      const value =
        item.key === MERGED
          ? mergedKeys.reduce((sum, key) => sum + (month.byGroup[key] ?? 0), 0)
          : (month.byGroup[item.key as GroupKey] ?? 0);
      values[item.key] = value;
      total += value;
    }
    return {
      key: month.month,
      label: monthShort(month.month),
      aria: monthName(month.month),
      values,
      tip: {
        title: `${monthName(month.month)}${month.partial ? ` (עד ${fmtDM(summary?.asOf ?? '')})` : ''} · ${fmtMoney(total)}`,
        rows: series
          .slice()
          .reverse()
          .map((item) => ({ color: `var(--series-${item.series})`, value: fmtMoney(values[item.key] ?? 0), label: item.label })),
      },
    };
  });
  const currentIndex = rows.findIndex((row) => data.monthly.find((m) => m.month === row.key)?.partial);

  return (
    <div className="screen">
      {isSub ? null : <PeriodChip summary={summary} inline />}
      <div className="g12">
        <section className={`sec${isSub ? ' only-d' : ''}`} style={{ ['--c' as string]: '12' } as Record<string, string>}>
          {data.period.multi ? (
            <div className="row">
              <Chip tone="muted">{he.period.lastMonthChip(monthName(data.period.lastMonth, false))}</Chip>
            </div>
          ) : null}
          <StackedColumns
            question={he.cats.stackedQ}
            period={`${monthName(rows[0]?.key ?? data.period.lastMonth, false)}–${monthName(data.period.lastMonth)}`}
            summary={`הוצאות חודשיות עד ${fmtMoney(Math.max(...data.monthly.map((m) => m.totalAgorot)))}`}
            series={series}
            rows={rows}
            currentIndex={currentIndex}
            table={{
              head: [he.cats.tblMonth, ...series.map((item) => item.label), he.cats.tblTotal],
              rows: data.monthly.map((month) => [
                monthName(month.month),
                ...series.map((item) =>
                  fmtMoney(item.key === MERGED ? mergedKeys.reduce((sum, key) => sum + (month.byGroup[key] ?? 0), 0) : month.byGroup[item.key as GroupKey] ?? 0),
                ),
                fmtMoney(month.totalAgorot),
              ]),
            }}
          />
        </section>

        <section className={`sec${isSub ? ' only-d' : ''}`} style={{ ['--c' as string]: '5' } as Record<string, string>} aria-label={he.cats.listTitle}>
          <div className="sec-h">
            <h2>{he.cats.listTitle}</h2>
            <span className="sub">{monthName(data.period.lastMonth)}</span>
          </div>
          <div className="catlist">
            {stats.map((stat) => (
              <CategoryRow key={stat.groupKey} stat={stat} selected={selected === stat.groupKey} />
            ))}
          </div>
        </section>

        <div className={isSub ? '' : 'only-d'} style={{ ['--c' as string]: '7' } as Record<string, string>}>
          <CategoryPanel groupKey={selected} />
        </div>

        {isSub ? null : <ReturnsSection data={data} />}
      </div>
    </div>
  );
}

function CategoryRow({ stat, selected }: { stat: CategoryStat; selected: boolean }) {
  const rowScale = Math.max(2 * stat.baseline.agorot, stat.monthAmountAgorot) || 1;
  const width = Math.min(100, (stat.monthAmountAgorot / rowScale) * 100);
  return (
    <Link className="catrow" to={buildPath('categories', stat.groupKey)} aria-current={selected ? 'true' : undefined}>
      <CategoryIcon groupKey={stat.groupKey} small />
      <span className="n">
        <span>{CATEGORY[stat.groupKey].label}</span>
        <span className="mini" aria-hidden="true">
          <i className={stat.over ? 'over' : ''} style={{ width: `${width.toFixed(0)}%` }} />
        </span>
      </span>
      <span className="a">
        <span className="num">{fmtMoney(stat.amountAgorot)}</span>
      </span>
      <span className="row" style={{ gap: '4px' }}>
        {stat.over && stat.ratio != null ? (
          <Chip tone="critical" icon="alert">
            {he.home.over(stat.ratio.toFixed(1))}
          </Chip>
        ) : stat.baseline.manual ? (
          <Chip tone="accent">{he.cats.manualBaseline}</Chip>
        ) : stat.baseline.partial ? (
          <Chip tone="muted">{stat.baseline.n ? he.cats.partialBaseline(stat.baseline.n) : he.cats.noBaselineChip}</Chip>
        ) : null}
        <Icon name="chevL" className="xs t3" />
      </span>
    </Link>
  );
}

function CategoryPanel({ groupKey }: { groupKey: GroupKey }) {
  const { api, period, toast, user } = useApp();
  const detail = useQuery<CategoryDetailResponse>(cacheKey('category', { key: groupKey, period }), () => api.category(groupKey, period), {
    scopes: ['categories', 'summary'],
  });
  const [editing, setEditing] = useState(false);

  if (!detail.data) return <div className="sk" style={{ height: '420px' }} />;
  const { stat, settings, trend, subcategories, merchants, period: resolved } = detail.data;
  const diff = stat.monthAmountAgorot - stat.baseline.agorot;
  const pct = stat.baseline.agorot ? (diff / stat.baseline.agorot) * 100 : null;
  const currentIndex = trend.findIndex((point) => point.partial);

  return (
    <Section
      variant="panel"
      className="cat-panel"
      titleId="h-cat"
      title={
        <span className="row" style={{ gap: '8px' }}>
          <CategoryIcon groupKey={groupKey} />
          {CATEGORY[groupKey].label}
          {stat.over && stat.ratio != null ? (
            <Chip tone="critical" icon="alert">
              {he.home.over(stat.ratio.toFixed(1))}
            </Chip>
          ) : stat.baseline.manual ? (
            <Chip tone="accent">{he.cats.manualBaseline}</Chip>
          ) : null}
        </span>
      }
      action={
        <Link className="link" to={buildPath('transactions', null, { cat: groupKey })}>
          {he.cats.allInCategory}
          <Icon name="chevL" className="xs" />
        </Link>
      }
    >
      {!stat.baseline.n && !stat.baseline.manual ? (
        <div className="notice">
          <Icon name="info" />
          <span>{he.cats.noBaselineYet}</span>
        </div>
      ) : null}

      {/* REDESIGN §8: ONE hero figure per screen, and every number carries its comparison.
          The three bordered tiles became the hero plus two plain figures — a bordered surface
          is only for a block you can act on or open. */}
      <div className="cat-hero">
        <span className="fk-meta">{resolved.multi ? monthName(resolved.lastMonth, false) : he.cats.thisMonth}</span>
        <p className={`fk-hero${stat.over ? ' critical' : ''}`}>
          <Money agorot={stat.monthAmountAgorot} />
        </p>
        <p className="fk-hero-inputs">
          {he.cats.baselineLabel(fmtMoney(stat.baseline.agorot))}
          {stat.ratio == null ? '' : ` · ${he.home.over(stat.ratio.toFixed(1))}`}
          {resolved.multi ? ` · ${he.cats.wholeRange(fmtMoney(stat.amountAgorot))}` : ''}
        </p>
      </div>

      <div className="loans-figs">
        <div className="fk-figure">
          <span className="fk-meta">{he.cats.avg3}</span>
          <span className="fk-figure__value">
            <Money agorot={stat.baseline.agorot} />
          </span>
          <span className="fk-figure__note">
            {stat.baseline.manual
              ? he.cats.manualBaseline
              : stat.baseline.months.length
                ? `${monthName(stat.baseline.months[0] ?? '', false)}–${monthName(stat.baseline.months[stat.baseline.months.length - 1] ?? '', false)}`
                : '—'}
          </span>
        </div>
        <div className="fk-figure">
          <span className="fk-meta">{he.cats.diff}</span>
          <span className={`fk-figure__value ${diff > 0 ? 'critical' : diff < 0 ? 'good' : ''}`}>
            {stat.baseline.agorot ? (
              <>
                <Icon name={diff > 0 ? 'upRight' : 'downLeft'} className="s" />
                <Money agorot={diff} plus />
              </>
            ) : (
              '—'
            )}
          </span>
          {pct !== null ? <span className="fk-figure__note">{fmtPct(pct, 0)}</span> : null}
        </div>
      </div>

      <Columns
        question={he.cats.trendQ(CATEGORY[groupKey].label)}
        period={`${monthName(trend[0]?.month ?? resolved.lastMonth, false)}–${monthName(resolved.lastMonth)}`}
        points={trend.map((point) => ({
          label: monthShort(point.month),
          value: point.amountAgorot,
          aria: `${monthName(point.month)} ${fmtMoney(point.amountAgorot)}`,
          tip: {
            title: monthName(point.month),
            rows: [
              { value: fmtMoney(point.amountAgorot), label: point.partial ? 'עד כה' : '' },
              ...(point.baselineAgorot ? [{ value: fmtMoney(point.baselineAgorot), label: he.cats.baselineThen('') }] : []),
            ],
          },
        }))}
        baseline={stat.baseline.agorot || undefined}
        baselineLabel={he.cats.baselineLabel(fmtMoney(stat.baseline.agorot))}
        currentIndex={currentIndex}
        step={100000}
        height={200}
        table={{
          head: [he.cats.tblMonth, he.cats.tblSpend, he.cats.tblBaseline],
          rows: trend.map((point) => [monthName(point.month), fmtMoney(point.amountAgorot), fmtMoney(point.baselineAgorot ?? 0)]),
        }}
      />

      <div className="stack" style={{ gap: '8px' }}>
        <div className="row between wrap">
          <span className="small">
            {he.cats.threshold(stat.thresholdPct)}
            {settings.manualBaselineAgorot ? ` · ${he.cats.thresholdManual(fmtMoney(settings.manualBaselineAgorot))}` : ''}
          </span>
          {user?.role === 'owner' ? (
            <button type="button" className="btn sm" aria-expanded={editing} onClick={() => setEditing((v) => !v)}>
              <Icon name="pencil" className="xs" />
              {he.app.change}
            </button>
          ) : null}
        </div>
        {editing ? (
          <form
            className="stack"
            style={{ gap: '10px', padding: '12px', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}
            onSubmit={(event) => {
              event.preventDefault();
              const form = event.currentTarget;
              const pctValue = Number((form.elements.namedItem('thr') as HTMLInputElement).value);
              const baseValue = Number((form.elements.namedItem('base') as HTMLInputElement).value);
              setEditing(false);
              api
                .updateCategory(groupKey, {
                  thresholdPct: Number.isFinite(pctValue) ? pctValue : null,
                  manualBaselineAgorot: baseValue > 0 ? Math.round(baseValue * 100) : null,
                })
                .then(() => {
                  detail.refetch();
                  toast(
                    he.cats.thresholdSaved(CATEGORY[groupKey].label, pctValue) + (baseValue > 0 ? he.cats.thresholdSavedBase(fmtMoney(Math.round(baseValue * 100))) : ''),
                  );
                });
            }}
          >
            <div className="row" style={{ gap: '12px' }}>
              <label htmlFor="thr-range" className="small" style={{ whiteSpace: 'nowrap' }}>
                {he.cats.thresholdPct}
              </label>
              <input type="range" id="thr-range" name="thr" min={0} max={100} step={5} defaultValue={stat.thresholdPct} />
            </div>
            <div className="row" style={{ gap: '12px' }}>
              <label htmlFor="thr-base" className="small" style={{ whiteSpace: 'nowrap' }}>
                {he.cats.manualBaselineField}
              </label>
              <input
                className="input"
                id="thr-base"
                name="base"
                type="number"
                inputMode="numeric"
                placeholder={he.cats.manualBaselinePlaceholder}
                defaultValue={settings.manualBaselineAgorot ? settings.manualBaselineAgorot / 100 : ''}
                style={{ width: '140px' }}
              />
            </div>
            <div className="row wrap" style={{ gap: '8px' }}>
              <button type="submit" className="btn primary sm">
                {he.app.save}
              </button>
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => {
                  setEditing(false);
                  api.updateCategory(groupKey, { thresholdPct: null, manualBaselineAgorot: null }).then(() => {
                    detail.refetch();
                    toast(he.cats.thresholdReset(CATEGORY[groupKey].label));
                  });
                }}
              >
                {he.cats.resetDefault}
              </button>
              <button type="button" className="btn ghost sm" onClick={() => setEditing(false)}>
                {he.app.cancel}
              </button>
            </div>
          </form>
        ) : null}
      </div>

      {subcategories.length ? (
        <div className="stack" style={{ gap: '6px' }}>
          <h3 style={{ fontSize: '14px' }}>{he.cats.subcats(monthName(resolved.lastMonth, false))}</h3>
          <div className="hairlist">
            {subcategories.map((sub) => (
              <div className="listrow" style={{ minHeight: '36px' }} key={sub.sub ?? 'none'}>
                <span className="main">
                  {/* Hebrew label from the contract; the raw Financy key is only the last resort. */}
                  <span className="name">{sub.label ?? sub.sub ?? he.cats.noSub}</span>
                  <span className="share" aria-hidden="true">
                    <i style={{ width: `${Math.round(sub.share * 100)}%` }} />
                  </span>
                </span>
                <span className="amt" style={{ fontWeight: 500 }}>
                  <span className="num tab">{fmtMoney(sub.amountAgorot)}</span> <span className="meta num">{Math.round(sub.share * 100)}%</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="notice">
          <Icon name="inbox" />
          <span>{he.cats.emptyPeriod}</span>
        </div>
      )}

      {merchants.length ? (
        <div className="stack" style={{ gap: '6px' }}>
          <h3 style={{ fontSize: '14px' }}>{he.cats.topMerchants}</h3>
          <div className="hairlist">
            {merchants.map((merchant) => (
              <Link
                className="listrow clickable rowlink"
                style={{ minHeight: '40px' }}
                key={merchant.merchant}
                to={buildPath('transactions', null, { cat: groupKey, q: merchant.merchant })}
              >
                <span className="main">
                  <span className="name">{merchant.merchant}</span>
                  <span className="sub">{plural(merchant.count, he.tx.txOne, `{n} ${he.tx.txMany}`)}</span>
                </span>
                <span className="amt" style={{ fontWeight: 500 }}>
                  <span className="num tab">{fmtMoney(merchant.amountAgorot)}</span>
                </span>
                <Icon name="chevL" className="xs t3" />
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </Section>
  );
}

function ReturnsSection({ data }: { data: CategoriesResponse }) {
  const months = data.returns.months;
  const total = data.returns.total;
  return (
    <Section
      span={12}
      titleId="h-ret"
      title={he.cats.returnsTitle}
      sub={he.cats.returnsSub}
      action={
        <Link className="link" to={buildPath('transactions', null, { kind: 'returns', period: '12m' })}>
          {he.cats.returnsAll}
          <Icon name="chevL" className="xs" />
        </Link>
      }
    >
      <div className="row wrap" style={{ gap: '12px 32px', alignItems: 'flex-start' }}>
        <div className="stat" style={{ minWidth: '150px' }}>
          <span className="l">{he.cats.returns6m}</span>
          <span className="v">
            <span className="num">{total.count}</span>
          </span>
          <span className="d">{total.count ? plural(total.count, he.cats.returnsOne, he.cats.returnsMany(total.count)) : he.cats.returnsNone}</span>
        </div>
        <div className="stat" style={{ minWidth: '150px' }}>
          <span className="l">{he.cats.feesPaid}</span>
          <span className="v">
            <Money agorot={total.feesAgorot} />
          </span>
          <span className="d">{he.cats.feesUnder}</span>
        </div>
        <div className="grow" style={{ minWidth: '260px' }}>
          <Columns
            question={he.cats.returnsQ}
            period={he.cats.returnsPeriod(`${monthName(months[0]?.month ?? '', false)}–${monthName(months[months.length - 1]?.month ?? '')}`)}
            points={months.map((month) => ({
              label: monthShort(month.month),
              value: month.feesAgorot,
              aria: `${monthName(month.month)} ${fmtMoney(month.feesAgorot)}`,
              tip: {
                title: monthName(month.month),
                rows: [
                  { value: String(month.count), label: month.count === 1 ? 'החזרה' : 'החזרות' },
                  { value: fmtMoney(month.feesAgorot), label: he.cats.tblFees },
                ],
              },
            }))}
            step={5000}
            minTop={10000}
            height={150}
            table={{
              head: [he.cats.tblMonth, he.cats.tblReturns, he.cats.tblFees],
              rows: months.map((month) => [monthName(month.month), String(month.count), fmtMoney(month.feesAgorot)]),
            }}
          />
        </div>
      </div>
    </Section>
  );
}
